import { Box, Typography, Chip, Button, styled, useTheme, useMediaQuery, Grid } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { Fragment, useState } from 'react';
import { ShareModal } from './ShareModal';
import { FavoriteDetailActivity } from './FavoriteDetailActivity';

export const StyledOutlinedButton = styled(Button)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#333',
  borderRadius: 50,
  padding: '8px 16px',
  border: '0.5px solid #E1E1E2',
  transition: 'all 0.3s ease',
  textTransform: 'none',
  '& .icon': {
    color: theme.palette.primary.main,
    transition: 'color 0.3s',
  },
  '&:hover': {
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    borderColor: 'transparent',
    '& .icon': {
      color: '#fff',
    },
  },
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem !important',
  fontWeight: 500,
  lineHeight: 1.4,
  color: theme.palette.common.black,

  [theme.breakpoints.down('lg')]: {
    fontSize: '2rem !important',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '1.4rem !important',
  },
}));

const Chips = ({ saleCount, rentCount, t, isMobile = false }) => {
  return (
    <Box
      display={'flex'}
      flexDirection={isMobile ? 'column' : 'row'}
      gap={1}
      width={'auto'}
      sx={{ fontSize: '1rem', fontWeight: 500, color: '#575758' }}
    >
      <Box
        sx={{
          display: 'flex',
          bgcolor: '#FCD5BD',
          borderRadius: 999,
          width: { xs: '100%', lg: '225px' },
          justifyContent: 'center',
          py: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 4,
            width: '100%',
            textAlign: 'center',
          }}
        >
          {t('sell')}{' '}
          <Box>
            <Box
              component="span"
              sx={{
                color: (theme) => theme.palette.primary.main,
                display: 'inline',
              }}
            >
              {saleCount}
            </Box>{' '}
            {t('listing')}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          bgcolor: '#D1E5B8',
          borderRadius: 999,
          width: { xs: '100%', lg: '250px' },
          justifyContent: 'center',
          py: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 4,
            width: '100%',
            textAlign: 'center',
          }}
        >
          {t('rent')}{' '}
          <Box>
            <Box
              component="span"
              sx={{
                color: '#6CAC19',
                display: 'inline',
              }}
            >
              {rentCount}
            </Box>{' '}
            {t('listing')}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ActionButtons = ({ t, justify = 'flex-end', setOpenShare, isProject, id, detail }) => {
  return (
    <Box display="flex" gap={2} justifyContent={justify}>
      <FavoriteDetailActivity isProject={isProject} id={id} details={detail} />
      <StyledOutlinedButton endIcon={<IosShareIcon className="icon" />} onClick={() => setOpenShare(true)}>
        {t('share')}
      </StyledOutlinedButton>
    </Box>
  );
};

const NearbyLocationBox = ({ t, nearbyLocation }) => {
  return (
    <Fragment>
      {nearbyLocation?.distance && nearbyLocation?.location && (
        <Box sx={{ mt: { sm: 0.5, md: 1 } }}>
          <Typography
            variant="body2"
            color={'#919192'}
          >
            <span style={{ color: '#F4762A' }}>{nearbyLocation.distance?.split(' ')?.[0]}</span>{' '}
            {`${nearbyLocation.distance?.split(' ')?.[1]} ${t('to')}`}{' '}
            <span style={{ color: '#F4762A' }}>{nearbyLocation.location}</span>
          </Typography>
        </Box>
      )}
    </Fragment>
  );
};

export const HeaderSection = ({ header, nearbyLocation, isProject, detail }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [openShare, setOpenShare] = useState(false);

  const { logoImage, title, address, saleCount = 0, rentCount = 0 } = header;

  const imgSize = isDesktop ? 146 : theme.breakpoints.down('md') ? 93 : 80;

  if (isDesktop) {
    return (
      <Grid container px={2}>
        <Grid item xs={12} display="flex" gap={2}>
          {/* Logo Image */}
          {logoImage && (
            <Box
              sx={{
                width: imgSize,
                height: imgSize,
                borderRadius: 2,
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: '#E1E1E2',
              }}
            >
              <Image
                src={logoImage}
                alt="Logo Image"
                width={imgSize}
                height={imgSize}
                style={{
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>
          )}
          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StyledTitle
              variant="h1"
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '125%',
              }}
            >
              {title}
            </StyledTitle>
            <Typography
              variant="body1"
              color="text.secondary"
              mt={1.5}
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {address}
            </Typography>

            {isProject ? (
              <>
                <NearbyLocationBox t={t} nearbyLocation={nearbyLocation} />
                <Grid container mt={1.5} alignItems="center">
                  <Grid item xs={6}>
                    <Chips saleCount={saleCount} rentCount={rentCount} t={t} />
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <ActionButtons
                      t={t}
                      setOpenShare={setOpenShare}
                      isProject={isProject}
                      id={isProject ? detail.project_ID : detail.property_ID}
                      detail={detail}
                    />
                  </Grid>
                </Grid>
              </>
            ) : (
              <Grid container>
                <Grid item xs={6}>
                  <NearbyLocationBox t={t} nearbyLocation={nearbyLocation} />
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <ActionButtons
                    t={t}
                    setOpenShare={setOpenShare}
                    isProject={isProject}
                    id={isProject ? detail.project_ID : detail.property_ID}
                    detail={detail}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Grid>

        <ShareModal
          open={openShare}
          onClose={() => setOpenShare(false)}
          url={typeof window !== 'undefined' ? window.location.href : ''}
          emailSubject={title}
        />
      </Grid>
    );
  }

  return (
    <Grid container alignItems={'center'} px={2}>
      <Grid item xs={12} display="flex" gap={2}>
        {logoImage && (
          <Box
            sx={{
              width: { xs: 80, sm: 93 },
              height: { xs: 80, sm: 93 },
              borderRadius: 2,
              overflow: 'hidden',
              flexShrink: 0,
              backgroundColor: '#E1E1E2',
            }}
          >
            <Image
              src={logoImage}
              alt="project"
              width={isMobile ? 80 : 93}
              height={isMobile ? 80 : 93}
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.4em',
              maxHeight: { xs: 80, sm: 93 },
            }}
          >
            {title}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} my={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {address}
            </Typography>
            <NearbyLocationBox t={t} nearbyLocation={nearbyLocation} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box display="flex" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
              <ActionButtons
                t={t}
                setOpenShare={setOpenShare}
                isProject={isProject}
                id={isProject ? detail.project_ID : detail.property_ID}
                detail={detail}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {isProject && (
        <Grid item xs={12} mt={1}>
          <Chips saleCount={saleCount} rentCount={rentCount} t={t} isMobile={isMobile} />
        </Grid>
      )}

      <ShareModal
        open={openShare}
        onClose={() => setOpenShare(false)}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        emailSubject={title}
      />
    </Grid>
  );
};
